import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
    ArrowTopRightOnSquareIcon,
    ClipboardDocumentIcon,
    PencilSquareIcon,
    SparklesIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';
import { careerDirectionApi } from '../careerDirection/api';

const LINKEDIN_LIMIT = 3000;
const BRAND_TAGS = ['#LancersTech', '#LancersTechInternship'];

const STYLES = [
    { key: 'achievement', label: 'Achievement', hint: 'Celebrate it, with key takeaways' },
    { key: 'story', label: 'Story', hint: 'Where you started and what changed' },
    { key: 'lessons', label: 'Lessons learned', hint: 'A numbered list of 3 lessons' }
];

const STATUS = {
    draft: { label: 'Draft', className: 'bg-ink-100 text-ink-700' },
    pending_mentor: { label: 'Waiting for mentor approval', className: 'bg-amber-50 text-amber-800' },
    pending_business: { label: 'Waiting for business approval', className: 'bg-amber-50 text-amber-800' },
    ready: { label: 'Ready to share', className: 'bg-sky-50 text-sky-800' },
    approved: { label: 'Approved — ready to share', className: 'bg-sky-50 text-sky-800' },
    published: { label: 'Published', className: 'bg-emerald-50 text-emerald-800' },
    internal_only: { label: 'Portfolio only', className: 'bg-ink-100 text-ink-700' },
    rejected: { label: 'Changes needed', className: 'bg-red-50 text-red-700' }
};

/** Highlight hashtags the way LinkedIn does. */
function PostText({ text }) {
    const parts = String(text || '').split(/(#[A-Za-z0-9_]+)/g);
    return (
        <p className="whitespace-pre-wrap text-[13.5px] leading-relaxed text-ink-900">
            {parts.map((part, i) => (part.startsWith('#')
                ? <span key={i} className="font-semibold text-[#0a66c2]">{part}</span>
                : <React.Fragment key={i}>{part}</React.Fragment>))}
        </p>
    );
}

function initials(user) {
    const f = (user && user.first_name) || '';
    const l = (user && user.last_name) || '';
    return `${f.charAt(0)}${l.charAt(0)}`.toUpperCase() || 'Me';
}

async function copyText(text, message = 'Post copied — paste it into LinkedIn') {
    try {
        await navigator.clipboard.writeText(text);
        toast.success(message);
    } catch {
        toast.error('Could not copy. Select the text and copy it manually.');
    }
}

/**
 * Opens LinkedIn's post box with the text already filled in. The `text`
 * parameter is not officially documented by LinkedIn, so the post is also
 * copied to the clipboard on click as a fallback. Replace with the LinkedIn
 * Posts API once API access is available.
 *
 * LinkedIn decodes the text and then reads it as part of a URL again, so a
 * single-encoded "?" ended the post there (losing the hashtags), and "#",
 * "&", "+" and "%" would too. Those characters are encoded twice so they
 * arrive intact.
 */
function linkedInShareUrl(text) {
    const encoded = encodeURIComponent(text || '').replace(/%(25|3F|23|26|2B)/g, '%25$1');
    return `https://www.linkedin.com/feed/?shareActive=true&text=${encoded}`;
}

export default function LinkedInPostsTab({ posts, milestones, generating, onGenerate, onSubmit, onPublish, onSave }) {
    const { user } = useAuth();
    const [careerDirection, setCareerDirection] = useState(null);
    const [milestoneId, setMilestoneId] = useState('');
    const [style, setStyle] = useState('achievement');

    useEffect(() => {
        careerDirectionApi.mine().then((d) => setCareerDirection(d && d.job_title)).catch(() => {});
    }, []);

    const author = {
        name: user ? [user.first_name, user.last_name].filter(Boolean).join(' ') : 'You',
        headline: careerDirection ? `${careerDirection} · LancersTech Intern` : 'LancersTech Intern',
        initials: initials(user)
    };

    return (
        <div className="space-y-6">
            <section className="rounded-2xl border border-brand-200/70 bg-gradient-to-br from-brand-50/70 to-white p-5">
                <div className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-700 text-white">
                        <SparklesIcon className="h-5 w-5" />
                    </span>
                    <div>
                        <h3 className="text-base font-semibold text-ink-950">Write a LinkedIn post</h3>
                        <p className="mt-0.5 text-sm text-ink-600">
                            Pick a milestone and a style. We'll write a complete post you can edit, with the LancersTech hashtags included.
                        </p>
                    </div>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_auto]">
                    <select
                        aria-label="Milestone"
                        value={milestoneId}
                        onChange={(e) => setMilestoneId(e.target.value)}
                        disabled={generating}
                        className="w-full rounded-lg border border-border bg-white px-3 py-2.5 text-sm text-ink-900 focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600/20"
                    >
                        <option value="">Choose a milestone…</option>
                        {milestones.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </select>
                    <button
                        type="button"
                        disabled={!milestoneId || generating}
                        onClick={async () => {
                            await onGenerate(Number(milestoneId), style);
                            setMilestoneId('');
                        }}
                        className="btn-primary justify-center disabled:opacity-50"
                    >
                        {generating ? 'Writing…' : 'Write my post'}
                    </button>
                </div>

                <div role="radiogroup" aria-label="Post style" className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
                    {STYLES.map((s) => (
                        <button
                            key={s.key} type="button" role="radio" aria-checked={style === s.key}
                            onClick={() => setStyle(s.key)}
                            className={`rounded-xl border px-3 py-2 text-left transition-colors ${style === s.key
                                ? 'border-brand-600 bg-white ring-1 ring-brand-600'
                                : 'border-border bg-white/70 hover:border-ink-300'}`}
                        >
                            <span className="block text-sm font-semibold text-ink-900">{s.label}</span>
                            <span className="block text-xs text-ink-500">{s.hint}</span>
                        </button>
                    ))}
                </div>
            </section>

            <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-ink-950">Your posts</h3>
                <p className="text-xs text-ink-500">Every post includes {BRAND_TAGS.join(' ')}</p>
            </div>

            {posts.length === 0 ? (
                <p className="rounded-xl border border-dashed border-border py-8 text-center text-sm text-ink-500">
                    No posts yet. Choose a milestone above, or document one on the Document Journey tab.
                </p>
            ) : (
                <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                    {posts.map((post) => (
                        <PostCard
                            key={post.id}
                            post={post}
                            author={author}
                            onSubmit={onSubmit}
                            onPublish={onPublish}
                            onSave={onSave}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function PostCard({ post, author, onSubmit, onPublish, onSave }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(post.content || '');
    const [saving, setSaving] = useState(false);
    const status = STATUS[post.status] || { label: post.status, className: 'bg-ink-100 text-ink-700' };
    const editable = ['draft', 'rejected'].includes(post.status);
    const text = editing ? draft : post.content || '';
    const length = text.length;
    const missingBrand = useMemo(
        () => BRAND_TAGS.filter((tag) => !new RegExp(`${tag}(?![A-Za-z0-9_])`, 'i').test(text)),
        [text]
    );

    const save = async () => {
        setSaving(true);
        const ok = await onSave(post.id, draft);
        setSaving(false);
        if (ok) setEditing(false);
    };

    return (
        <article className="flex flex-col overflow-hidden rounded-2xl border border-border bg-white">
            <header className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
                <div className="min-w-0">
                    <h4 className="truncate text-sm font-semibold text-ink-950">{post.title}</h4>
                    <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${status.className}`}>{status.label}</span>
                </div>
                {editable && !editing && (
                    <button type="button" onClick={() => { setDraft(post.content || ''); setEditing(true); }} className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-ink-600 hover:text-brand-700">
                        <PencilSquareIcon className="h-4 w-4" /> Edit
                    </button>
                )}
            </header>

            <div className="flex-1 px-4 py-4">
                <div className="mb-3 flex items-center gap-2.5">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-700 text-sm font-semibold text-white">{author.initials}</span>
                    <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-ink-950">{author.name}</p>
                        <p className="truncate text-xs text-ink-500">{author.headline}</p>
                    </div>
                </div>
                {editing ? (
                    <textarea
                        aria-label="Post text"
                        rows={14}
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        className="w-full resize-y rounded-lg border border-border px-3 py-2 text-[13.5px] leading-relaxed focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600/20"
                    />
                ) : (
                    <PostText text={text} />
                )}
                <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[11px]">
                    <span className={length > LINKEDIN_LIMIT ? 'font-semibold text-red-700' : 'text-ink-400'}>
                        {length.toLocaleString()} / {LINKEDIN_LIMIT.toLocaleString()} characters
                    </span>
                    {editing && missingBrand.length > 0 && (
                        <span className="text-ink-500">{missingBrand.join(' ')} will be added when you save</span>
                    )}
                </div>
            </div>

            <footer className="flex flex-wrap items-center gap-2 border-t border-border bg-ink-50/60 px-4 py-3">
                {editing ? (
                    <>
                        <button type="button" onClick={save} disabled={saving || !draft.trim() || length > LINKEDIN_LIMIT} className="btn-primary disabled:opacity-50">
                            {saving ? 'Saving…' : 'Save'}
                        </button>
                        <button type="button" onClick={() => setEditing(false)} className="btn-secondary">Cancel</button>
                    </>
                ) : (
                    <>
                        <button type="button" onClick={() => copyText(post.content || '')} className="btn-secondary inline-flex items-center gap-1.5">
                            <ClipboardDocumentIcon className="h-4 w-4" /> Copy
                        </button>
                        <a
                            href={linkedInShareUrl(post.content)}
                            target="_blank"
                            rel="noreferrer"
                            onClick={() => copyText(post.content || '', 'LinkedIn is opening with your post — it\'s also copied in case the box is empty')}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-[#0a66c2] px-3.5 py-2 text-sm font-semibold text-white hover:bg-[#004182]"
                        >
                            <ArrowTopRightOnSquareIcon className="h-4 w-4" /> Post on LinkedIn
                        </a>
                        <span className="flex-1" />
                        {post.status === 'draft' && (
                            <button type="button" onClick={() => onSubmit(post.id)} className="btn-primary">Submit</button>
                        )}
                        {['ready', 'approved'].includes(post.status) && (
                            <button type="button" onClick={() => onPublish(post.id)} className="btn-primary">Mark as published</button>
                        )}
                    </>
                )}
            </footer>
        </article>
    );
}
